import React, { useEffect, useRef, useState } from 'react';

/* ── Reusable animated counter ── */
function Counter({ target, suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(prog * target));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

/* ── AI Chat mockup (dark product UI) ── */
function AIChatMockup() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ background: '#16122E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />)}
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>AI Ad Builder</span>
      </div>
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px 12px 2px 12px', padding: '8px 12px', maxWidth: '75%', fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
            Create a Meta ad for our Summer Sale — target 25-34 women in France
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: '#221C51', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, color: '#818CF8' }}>✦</div>
          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px 12px 12px 12px', padding: '8px 12px', maxWidth: '80%' }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: '0 0 8px', lineHeight: 1.5 }}>Generated your Summer Sale ad:</p>
            <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 11, color: '#818CF8', marginBottom: 4, fontWeight: 700 }}>HEADLINE</div>
              <div style={{ fontSize: 12, color: '#fff' }}>Up to 50% Off — Summer Sale Ends Soon</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 10px', marginTop: 6 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontWeight: 700 }}>BODY</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>Shop the looks everyone's talking about. Free shipping + easy returns.</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: '#221C51', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, color: '#818CF8' }}>✦</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Launching to Meta{dots}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Multi-platform dashboard mockup ── */
function PlatformMockup() {
  const platforms = [
    { name: 'Meta', spend: '€1,240', roas: '3.2x', status: 'live' },
    { name: 'TikTok', spend: '€890', roas: '2.8x', status: 'live' },
    { name: 'LinkedIn', spend: '€560', roas: '4.1x', status: 'paused' },
    { name: 'Snapchat', spend: '€340', roas: '2.1x', status: 'live' },
  ];
  return (
    <div style={{ background: '#16122E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />)}
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Campaign Manager — All Platforms</span>
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
          {[{ label: 'Total Spend', val: '€3,030' }, { label: 'Avg. ROAS', val: '3.05x' }, { label: 'Impressions', val: '128K' }, { label: 'Active Ads', val: '14' }].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk',sans-serif" }}>{s.val}</div>
            </div>
          ))}
        </div>
        {platforms.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', marginBottom: 6, border: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: p.status === 'live' ? '#818CF8' : 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', flex: 1, fontWeight: 600 }}>{p.name}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{p.spend}</span>
            <span style={{ fontSize: 11, color: '#818CF8', fontWeight: 700 }}>{p.roas}</span>
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: p.status === 'live' ? 'rgba(129,140,248,0.12)' : 'rgba(255,255,255,0.06)', color: p.status === 'live' ? '#818CF8' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{p.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Analytics chart mockup ── */
function AnalyticsMockup() {
  const bars = [40, 65, 50, 80, 72, 95, 88, 100, 92, 78, 85, 98];
  return (
    <div style={{ background: '#16122E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />)}
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Live Analytics — Last 30 days</span>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {[{ label: 'CTR', val: '4.8%', delta: '+0.6%', up: true }, { label: 'CPA', val: '€4.20', delta: '-12%', up: false }, { label: 'ROAS', val: '3.4x', delta: '+0.8x', up: true }].map((m, i) => (
            <div key={i} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 3 }}>{m.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk',sans-serif" }}>{m.val}</div>
              <div style={{ fontSize: 10, color: '#818CF8', marginTop: 2 }}>{m.delta}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
          {bars.map((h, i) => (
            <div key={i} style={{ flex: 1, borderRadius: '3px 3px 0 0', background: i === bars.length - 1 ? '#818CF8' : 'rgba(255,255,255,0.12)', height: `${h}%`, transition: `height 0.6s ${i * 50}ms ease` }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          {['Apr 1', '', '', 'Apr 8', '', '', 'Apr 15', '', '', 'Apr 22', '', 'Apr 30'].map((l, i) => (
            <span key={i} style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Automation rules mockup ── */
function AutomationMockup() {
  const rules = [
    { if: 'CPA > €8', then: 'Pause ad set', triggered: 2 },
    { if: 'ROAS > 4x', then: 'Increase budget +20%', triggered: 5 },
    { if: 'CTR < 1%', then: 'Alert me via email', triggered: 1 },
  ];
  return (
    <div style={{ background: '#16122E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}>
      <div style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />)}
        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Automation Rules</span>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 12px', background: 'rgba(129,140,248,0.06)', border: '1px solid rgba(129,140,248,0.2)', borderRadius: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#818CF8' }} />
          <span style={{ fontSize: 11, color: '#818CF8' }}>3 rules active · 8 actions triggered this week</span>
        </div>
        {rules.map((r, i) => (
          <div key={i} style={{ padding: '12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#818CF8', background: 'rgba(129,140,248,0.1)', padding: '2px 7px', borderRadius: 4 }}>IF</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>{r.if}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.08)', padding: '2px 7px', borderRadius: 4 }}>THEN</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>{r.then}</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>×{r.triggered}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Feature row ── */
function FeatureRow({ tag, headline, headlineAccent, sub, bullets, stats, visual, reverse, visible }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))',
      gap: 56,
      alignItems: 'center',
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(32px)',
      transition: 'all 0.7s ease',
    }}>
      <div style={{ order: reverse ? 2 : 1 }}>
        <div className="mono-label" style={{ color: '#737373', marginBottom: 20 }}>
          <span style={{ color: '#4F46E5' }}>/</span> {tag}
        </div>
        <h3 style={{ fontSize: 'clamp(24px,3vw,36px)', fontWeight: 700, color: '#111', letterSpacing: '-0.03em', marginBottom: 14, lineHeight: 1.15 }}>
          {headline}
          {headlineAccent && (
            <em className="serif-accent">
              {' '}{headlineAccent}
            </em>
          )}
        </h3>
        <p style={{ fontSize: 15, color: '#737373', lineHeight: 1.7, marginBottom: 24 }}>{sub}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: '#404040' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#111', flexShrink: 0 }} />
              {b}
            </li>
          ))}
        </ul>
        {stats && (
          <div style={{ display: 'flex', gap: 28, marginTop: 32 }}>
            {stats.map((s, i) => (
              <div key={i} style={{ paddingRight: i < stats.length - 1 ? 28 : 0, borderRight: i < stats.length - 1 ? '1px solid #E5E6EF' : 'none' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#111', letterSpacing: '-0.02em', fontFamily: "'Space Grotesk',sans-serif" }}>
                  <Counter target={s.num} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 12, color: '#A3A3A3', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ order: reverse ? 1 : 2 }}>
        {visual}
      </div>
    </div>
  );
}

const Features = () => {
  const [v1, setV1] = useState(false);
  const [v2, setV2] = useState(false);
  const [v3, setV3] = useState(false);
  const [v4, setV4] = useState(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);

  useEffect(() => {
    const pairs = [[ref1, setV1], [ref2, setV2], [ref3, setV3], [ref4, setV4]];
    pairs.forEach(([r, setter]) => {
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setter(true); }, { threshold: 0.15 });
      if (r.current) obs.observe(r.current);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section id="features" style={{ background: '#F6F7FB', padding: '110px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Section header */}
        <div style={{ marginBottom: 90 }}>
          <div className="mono-label" style={{ color: '#737373', marginBottom: 22 }}>
            <span style={{ color: '#4F46E5' }}>/</span> Features
          </div>
          <h2 style={{ fontSize: 'clamp(32px,4.5vw,54px)', fontWeight: 700, color: '#111', letterSpacing: '-0.035em', marginBottom: 16, lineHeight: 1.08, maxWidth: 720 }}>
            Your entire ad stack,{' '}
            <em className="serif-accent">unified.</em>
          </h2>
          <p style={{ fontSize: 16, color: '#737373', maxWidth: 460, lineHeight: 1.65 }}>
            Built for media buyers who need to move fast, scale reliably, and never leave money on the table.
          </p>
        </div>

        {/* Feature row 1 — AI Content */}
        <div ref={ref1} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v1}
            tag="AI-powered"
            headline="Write winning ad copy"
            headlineAccent="in seconds."
            sub="Describe your product and audience — our AI generates platform-optimised headlines, body copy, and CTAs instantly. No briefs, no back-and-forth."
            bullets={[
              'Platform-specific copy for Meta, TikTok, LinkedIn, Snapchat',
              'A/B variant generation with a single click',
              'Tone & brand voice customisation',
              'Multilingual support (20+ languages)',
            ]}
            stats={[
              { num: 80, suffix: '%', label: 'less time writing copy' },
              { num: 3, suffix: 'x', label: 'more ad variations tested' },
            ]}
            visual={<AIChatMockup />}
          />
        </div>

        {/* Feature row 2 — Multi-platform */}
        <div ref={ref2} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v2}
            reverse
            tag="Multi-platform"
            headline="One campaign, six platforms,"
            headlineAccent="one click."
            sub="Connect your Meta, TikTok, Google, LinkedIn, Snapchat, and Taboola accounts — then launch cross-platform campaigns without jumping between dashboards."
            bullets={[
              'Unified campaign view across all ad accounts',
              'Platform-specific targeting adapted automatically',
              'Centralised budget control and pacing',
              'Real-time sync — no manual refresh needed',
            ]}
            stats={[
              { num: 6, suffix: '+', label: 'platforms connected' },
              { num: 70, suffix: '%', label: 'faster campaign setup' },
            ]}
            visual={<PlatformMockup />}
          />
        </div>

        {/* Feature row 3 — Analytics */}
        <div ref={ref3} style={{ marginBottom: 100 }}>
          <FeatureRow
            visible={v3}
            tag="Live analytics"
            headline="See what's working"
            headlineAccent="before money is wasted."
            sub="Live metrics across every campaign — CTR, CPA, ROAS, spend, and impressions — all in one place, updating in real time."
            bullets={[
              'Real-time spend and ROAS per platform',
              'Campaign drill-down: ad sets → individual ads',
              'Historical trend charts and CSV export',
              'Custom date ranges and filtering',
            ]}
            stats={[
              { num: 128, suffix: 'K', label: 'impressions tracked daily' },
              { num: 98, suffix: '%', label: 'data accuracy' },
            ]}
            visual={<AnalyticsMockup />}
          />
        </div>

        {/* Feature row 4 — Automation */}
        <div ref={ref4}>
          <FeatureRow
            visible={v4}
            reverse
            tag="Automation"
            headline="Set rules."
            headlineAccent="Let the platform optimise."
            sub="Create IF/THEN automation rules that fire 24/7 — pause underperforming ads, scale winners automatically, and get notified before budgets overspend."
            bullets={[
              '"Pause if CPA > €8" — auto-enforced',
              'Budget scaling rules on high-ROAS campaigns',
              'Email & in-app alerts on threshold breaches',
              'Rule history and audit log',
            ]}
            stats={[
              { num: 24, suffix: '/7', label: 'always monitoring' },
              { num: 40, suffix: '%', label: 'avg. CPA reduction' },
            ]}
            visual={<AutomationMockup />}
          />
        </div>

      </div>
    </section>
  );
};

export default Features;
